import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AiVoiceExtraction, ThreatTag, ActionTag, SupportTag, Showing, Strategy } from '@duty/shared';
import type { AmbientTranscriptHook } from './useAmbientTranscript';
import { useTranscriptSource } from './useTranscriptSource';

type VoiceExtractResponse = { aiUsed: true; extraction: AiVoiceExtraction } | { aiUsed: false; reason?: string };

/**
 * How often to check for new speech and re-extract while listening —
 * continuous, not on-click, but rate-limited so a long unbroken transmission
 * doesn't call the AI endpoint every render. Bounded AI cost, not a manual
 * trigger the officer has to remember to press.
 */
const AUTO_EXTRACT_INTERVAL_MS = 8000;

export interface VoiceSuggestions {
  transcript: AmbientTranscriptHook;
  extracting: boolean;
  unavailableReason?: string;
  threatQuotes: Partial<Record<ThreatTag, string>>;
  actionQuotes: Partial<Record<ActionTag, string>>;
  supportQuotes: Partial<Record<SupportTag, string>>;
  showingSuggestion?: { value: Showing; quote: string };
  strategySuggestion?: { value: Strategy; quote: string };
}

/**
 * Continuously (not on-click) turns the live ambient transcript into ITASC
 * tag suggestions. There is deliberately no separate "review and apply"
 * step here — the suggestions are consumed as highlight indicators directly
 * on the existing ITASC chip pickers (see `ItascCapture`/`controls.tsx`'s
 * `suggested` prop): accepting a suggestion is just clicking the
 * already-interactive chip, the same action as manual entry. Same grounding
 * discipline as the pager AI-extraction fallback — see `voice-extraction.ts`.
 */
export function useVoiceSuggestions(): VoiceSuggestions {
  const transcript = useTranscriptSource();
  const [extraction, setExtraction] = useState<AiVoiceExtraction | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState<string | undefined>(undefined);

  const inFlightRef = useRef(false);
  const lastExtractedSegmentCountRef = useRef(0);
  const segmentCountRef = useRef(0);
  const transcriptTextRef = useRef('');

  const transcriptText = useMemo(() => transcript.segments.map((s) => s.text).join(' '), [transcript.segments]);
  transcriptTextRef.current = transcriptText;
  segmentCountRef.current = transcript.segments.length;

  const runExtraction = useCallback(async () => {
    const text = transcriptTextRef.current;
    if (!text.trim() || inFlightRef.current) return;
    inFlightRef.current = true;
    setExtracting(true);
    try {
      const res = await fetch('/api/voice-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: text }),
        signal: AbortSignal.timeout(15000),
      });
      const data = (await res.json()) as VoiceExtractResponse;
      if (data.aiUsed) {
        setExtraction(data.extraction);
        setUnavailableReason(undefined);
      } else {
        setUnavailableReason(data.reason);
      }
    } catch {
      setUnavailableReason('Request failed');
    } finally {
      setExtracting(false);
      inFlightRef.current = false;
    }
  }, []);

  // Auto-extraction loop: while listening, periodically check whether new speech has been
  // finalised since the last call and, if so, re-extract with the full transcript so far.
  useEffect(() => {
    if (!transcript.listening) return;
    const id = setInterval(() => {
      if (segmentCountRef.current > lastExtractedSegmentCountRef.current) {
        lastExtractedSegmentCountRef.current = segmentCountRef.current;
        void runExtraction();
      }
    }, AUTO_EXTRACT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [transcript.listening, runExtraction]);

  // Reset suggestions (and the "already extracted through" marker) when the transcript is
  // cleared/restarted, so a fresh session doesn't carry over stale suggestions.
  useEffect(() => {
    if (transcript.segments.length === 0) {
      setExtraction(null);
      lastExtractedSegmentCountRef.current = 0;
    }
  }, [transcript.segments.length]);

  const threatQuotes = useMemo(
    () => Object.fromEntries((extraction?.threats ?? []).map((s) => [s.tag, s.quote])) as Partial<Record<ThreatTag, string>>,
    [extraction],
  );
  const actionQuotes = useMemo(
    () => Object.fromEntries((extraction?.actions ?? []).map((s) => [s.tag, s.quote])) as Partial<Record<ActionTag, string>>,
    [extraction],
  );
  const supportQuotes = useMemo(
    () => Object.fromEntries((extraction?.support ?? []).map((s) => [s.tag, s.quote])) as Partial<Record<SupportTag, string>>,
    [extraction],
  );

  return {
    transcript,
    extracting,
    unavailableReason,
    threatQuotes,
    actionQuotes,
    supportQuotes,
    showingSuggestion: extraction?.showing,
    strategySuggestion: extraction?.strategy,
  };
}
